import { Component, OnInit, ViewChild } from '@angular/core';
import { SidePanelService } from '@app/shared/components/side-panel/side-panel.service';
import { ManualPage } from '@app/shared/models/enums/manualPage.enum';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SmsStatisticsResponse } from 'src/_api/models/sms-statistics-response';
import { SmsStatisticData } from 'src/_api/models/sms-statistics-data';
import { MonthTranslations } from '@app/models/month-translation';
import { Customer, CustomerApi, StatisticsApi, TimeframeEnum } from '@api/index';
import { AuthService } from '@app/core/auth/auth.service';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { EnumService } from '@app/core/services/enum.service';
import { TranslateConfigService } from '@app/core/translate-config.service';
import { DateTime } from 'luxon';
import { getTimeFrameFunctionMap, toStartOfDayIso, toEndOfDayIso } from '@app/shared/utils/timeframe.utils';

@Component({
  selector: 'flow-sms',
  templateUrl: './sms.component.html',
  styleUrls: ['./sms.component.scss'],
})
export class SmsComponent implements OnInit {
  form: FormGroup;
  timeframeTypes = [];
  startDate: any;
  endDate: any;
  isAdmin: boolean;
  customerId: number;
  customers: Customer[];
  chartData: SmsStatisticsResponse;
  topRecipient: { noSmsForTopRecipinet: number; topRecipient: string };

  displayedColumns: string[] = ['name', 'emailAddress', 'phoneNumber', 'smsNotifications', 'status'];
  dataSource: MatTableDataSource<SmsStatisticData>;
  @ViewChild(MatSort) sort: MatSort;

  public translations = {
    totalNumberOfSms: 'sms.totalSms',
    topRecipient: 'sms.topRecipient',
    estimatedCostOfSmsNotifications: 'sms.estimatedCostOfSmsNotifications',
  };

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private enumService: EnumService,
    private translateConfigService: TranslateConfigService,
    private statisticsApi: StatisticsApi,
    private customerApi: CustomerApi,
    private sidePanelService: SidePanelService
  ) {
    this.timeframeTypes = this.enumService.timeframeTypes;
  }

  toggleSidePanel() {
    const config = {
      title: 'contextualModal.sms.title',
      manualKey: ManualPage.Sms,
      children: [
        {
          title: 'contextualModal.sms.whatIsThis.title',
          description: 'contextualModal.sms.whatIsThis.description',
        },
        {
          title: 'contextualModal.sms.whyIsItImportant.title',
          description: 'contextualModal.sms.whyIsItImportant.description',
        },
        {
          title: 'contextualModal.sms.howDoIUseThisFeature.title',
          description: 'contextualModal.sms.howDoIUseThisFeature.description',
        },
      ],
    };

    this.sidePanelService.openSidePanel(config);
  }

  ngOnInit(): void {
    const user = this.authService.userData;
    this.isAdmin = user.roleId === UserRoleEnum.Admin && !user.currentCustomerId;
    this.customerId = user?.currentCustomerId || user?.customerId;
    if (this.isAdmin) {
      this.customerApi.getCustomers().subscribe(data => (this.customers = data));
    }
    this.initForm();
    this.getSmsData();
  }

  translateMonth(month: string) {
    if (month && month.length > 0) return `${this.translateConfigService.instant(MonthTranslations[month])}`;
    else return '';
  }

  checkTimeFrame() {
    return this.form.get('timeframe')?.value === TimeframeEnum.Custom;
  }

  clearDatePickerFrom() {
    this.form.get('start').setValue('');
  }

  clearDatePickerTo() {
    this.form.get('end').setValue('');
  }

  private getSmsData() {
    const timeframe = this.form.get('timeframe').value;
    let isCustomer = this.form.get('customer').value;
    if (!isCustomer) return;

    let params = {
      ...this.form.value,
      customerId: this.form.get('customer').value,
    };

    if (timeframe === TimeframeEnum.Custom) {
      let invalidDate =
        this.form.get('start').value == '' ||
        this.form.get('end').value == '' ||
        this.form.get('start').value == null ||
        this.form.get('end').value == null;
      if (invalidDate) return;

      params = {
        ...params,
        startDate: toStartOfDayIso(this.form.get('start').value as DateTime),
        endDate: toEndOfDayIso(this.form.get('end').value as DateTime),
      };
    }

    if (timeframe !== TimeframeEnum.Custom) {
      const timeframeFunctionMap = getTimeFrameFunctionMap();

      const dateRangeFn = timeframeFunctionMap[timeframe];
      if (dateRangeFn) {
        const [start, end] = dateRangeFn();
        params = {
          ...params,
          startDate: toStartOfDayIso(DateTime.fromISO(start)),
          endDate: toEndOfDayIso(DateTime.fromISO(end)),
        };
      }
    }

    this.statisticsApi.getSmsStatistics(params).subscribe((data: SmsStatisticsResponse) => {
      this.dataSource = new MatTableDataSource(data.smsStatistics);
      this.chartData = data;
      this.topRecipient = { noSmsForTopRecipinet: data.noSmsForTopRecipinet, topRecipient: data.topRecipient };
    });
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      timeframe: [3],
      start: [''],
      end: [''],
      customer: [this.customerId],
    });
    this.form.get('end').valueChanges.subscribe(() => {
      this.getSmsData();
    });
    this.form.get('customer').valueChanges.subscribe(() => this.getSmsData());

    this.form.get('timeframe').valueChanges.subscribe(() => {
      const timeframe = this.form.get('timeframe').value;
      if (timeframe === TimeframeEnum.Custom) {
        this.form.get('start').setValidators(Validators.required);
        this.form.get('end').setValidators(Validators.required);
        this.startDate = '';
        this.endDate = '';
      }
      if (timeframe !== TimeframeEnum.Custom) {
        this.form.get('start').removeValidators([Validators.required]);
        this.form.get('end').removeValidators([Validators.required]);
        this.form.get('start').setValue(null);
        this.form.get('end').setValue(null);
        const timeframeFunctionMap = getTimeFrameFunctionMap();

        const dateRangeFn = timeframeFunctionMap[timeframe];
        if (dateRangeFn) {
          const [startDate, endDate] = dateRangeFn();
          this.startDate = startDate;
          this.endDate = endDate;
        }
      }
    });
  }
}
